
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  RefreshCw,
  Trash2,
  Mail,
  Phone,
  Eye,
  MessageSquareText,
  Plus,
  Check,
  X,
  Clock3,
  CheckCircle2,
  AlertCircle,
  CalendarDays,
  UserRound,
  Filter,
  ChevronDown,
  ExternalLink,
  Inbox,
  CircleDot,
} from "lucide-react";

import Modal from "../components/Modal";
import { PageHeader, StatCard } from "../components/ui";

import {
  EmptyState,
  ErrorMessage,
  Loading,
  SuccessMessage,
} from "../components/Feedback";

import {
  api,
  getErrorMessage,
  unwrapList,
} from "../services/api";

/* =========================================================
   CONSTANTS
========================================================= */

const STATUS_OPTIONS = [
  {
    value: "new",
    label: "New",
    icon: CircleDot,
    classes: "bg-accent-50 text-accent-700 border-accent-100",
    dot: "bg-accent-600",
  },
  {
    value: "in-progress",
    label: "In progress",
    icon: Clock3,
    classes: "bg-amber-50 text-amber-700 border-amber-100",
    dot: "bg-amber-500",
  },
  {
    value: "resolved",
    label: "Resolved",
    icon: CheckCircle2,
    classes: "bg-emerald-50 text-emerald-700 border-emerald-100",
    dot: "bg-emerald-500",
  },
];

const inputClass = "input";

const statusClasses = {
  new: "bg-accent-50 text-accent-700 border-accent-100",
  "in-progress": "bg-amber-50 text-amber-700 border-amber-100",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-100",
};

/* =========================================================
   HELPERS
========================================================= */

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getStatus(status) {
  return (
    STATUS_OPTIONS.find((option) => option.value === status) ||
    STATUS_OPTIONS[0]
  );
}

/* =========================================================
   STATUS BADGE
========================================================= */

function StatusBadge({ status }) {
  const config = getStatus(status);
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold ${config.classes}`}
    >
      <Icon size={13} />
      {config.label}
    </span>
  );
}

/* =========================================================
   STAT CARD
========================================================= */

/* =========================================================
   CONTACT AVATAR
========================================================= */

function ContactAvatar({ name }) {
  const initials = (name || "Guest")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-brand-100 bg-brand-50 text-sm font-semibold text-brand-700">
      {initials || "G"}
    </div>
  );
}

/* =========================================================
   CONTACT INFO
========================================================= */

function ContactInfo({ item }) {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <ContactAvatar name={item.name} />

      <div className="min-w-0 flex-1">
        <p className="break-words font-semibold text-slate-900">
          {item.name || "Unknown contact"}
        </p>

        {item.email && (
          <a
            href={`mailto:${item.email}`}
            className="mt-1 flex items-center gap-1.5 break-all text-xs text-slate-500 transition hover:text-brand-800"
          >
            <Mail size={12} className="shrink-0" />
            {item.email}
          </a>
        )}

        {item.mobile && (
          <a
            href={`tel:${item.mobile}`}
            className="mt-1 flex items-center gap-1.5 text-xs text-slate-500 transition hover:text-brand-800"
          >
            <Phone size={12} className="shrink-0" />
            {item.mobile}
          </a>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   MESSAGE PREVIEW
========================================================= */

function MessagePreview({ message }) {
  return (
    <p className="mt-1 line-clamp-2 max-w-sm whitespace-pre-wrap text-xs leading-5 text-slate-500">
      {message || "No message provided."}
    </p>
  );
}

/* =========================================================
   CONTACT DETAILS FIELD
========================================================= */

function DetailField({ label, value, icon: Icon }) {
  return (
    <div className="min-w-0 rounded-lg border border-slate-100 bg-white p-3.5">
      <p className="text-xs font-semibold  text-slate-500">
        {label}
      </p>

      <div className="mt-2 flex items-start gap-2">
        {Icon && (
          <Icon
            size={15}
            className="mt-0.5 shrink-0 text-slate-500"
          />
        )}

        <p className="break-words text-sm font-medium text-slate-800">
          {value || "—"}
        </p>
      </div>
    </div>
  );
}

/* =========================================================
   MAIN CONTACTS PAGE
========================================================= */

export default function Contacts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /* ---------------------------------------------------------
     LOAD CONTACT ENQUIRIES
  --------------------------------------------------------- */

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/api/contact-us");

      setItems(unwrapList(response));
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ---------------------------------------------------------
     UPDATE STATUS
  --------------------------------------------------------- */

  const updateStatus = async (item, newStatus) => {
    if (
      !item?._id ||
      saving ||
      item.status === newStatus
    ) {
      return;
    }

    const previousStatus = item.status || "new";

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const response = await api.patch(
        `/api/contact-us/${item._id}/status`,
        {
          status: newStatus,
        }
      );

      const updated =
        response.data?.data ||
        response.data?.contact ||
        {};

      const updatedItem = {
        ...item,
        ...updated,
        status: newStatus,
      };

      setItems((previous) =>
        previous.map((contact) =>
          contact._id === item._id
            ? { ...contact, ...updatedItem }
            : contact
        )
      );

      setSelected((previous) =>
        previous?._id === item._id
          ? { ...previous, ...updatedItem }
          : previous
      );

      setSuccess(
        `Enquiry status changed to ${getStatus(newStatus).label}.`
      );
    } catch (requestError) {
      setError(getErrorMessage(requestError));

      // Preserve the old status in the list if the update failed.
      setItems((previous) =>
        previous.map((contact) =>
          contact._id === item._id
            ? { ...contact, status: previousStatus }
            : contact
        )
      );
    } finally {
      setSaving(false);
    }
  };

  /* ---------------------------------------------------------
     DELETE ENQUIRY
  --------------------------------------------------------- */

  const remove = async () => {
    if (!deleteTarget || deleting) return;

    setDeleting(true);
    setError("");
    setSuccess("");

    try {
      await api.delete(
        `/api/contact-us/${deleteTarget._id}`
      );

      setItems((previous) =>
        previous.filter(
          (item) => item._id !== deleteTarget._id
        )
      );

      setSelected((previous) =>
        previous?._id === deleteTarget._id
          ? null
          : previous
      );

      setSuccess("Contact enquiry deleted successfully.");
      setDeleteTarget(null);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  /* ---------------------------------------------------------
     FILTER AND SORT
  --------------------------------------------------------- */

  const filtered = useMemo(() => {
    return items
      .filter((item) => {
        const searchText = [
          item.name,
          item.email,
          item.mobile,
          item.subject,
          item.message,
        ]
          .join(" ")
          .toLowerCase();

        const matchesQuery = searchText.includes(
          query.trim().toLowerCase()
        );

        const matchesStatus =
          status === "all" || (item.status || "new") === status;

        return matchesQuery && matchesStatus;
      })
      .sort((a, b) => {
        if (sortBy === "oldest") {
          return (
            new Date(a.createdAt || 0).getTime() -
            new Date(b.createdAt || 0).getTime()
          );
        }

        if (sortBy === "name") {
          return (a.name || "").localeCompare(b.name || "");
        }

        return (
          new Date(b.createdAt || 0).getTime() -
          new Date(a.createdAt || 0).getTime()
        );
      });
  }, [items, query, status, sortBy]);

  /* ---------------------------------------------------------
     STATISTICS
  --------------------------------------------------------- */

  const statistics = useMemo(() => {
    const newCount = items.filter(
      (item) => (item.status || "new") === "new"
    ).length;

    const inProgressCount = items.filter(
      (item) => item.status === "in-progress"
    ).length;

    const resolvedCount = items.filter(
      (item) => item.status === "resolved"
    ).length;

    return {
      total: items.length,
      newCount,
      inProgressCount,
      resolvedCount,
    };
  }, [items]);

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="space-y-6 pb-8">
      {/* PAGE HEADER */}
      <PageHeader
        title="Contact enquiries"
        description="Review website messages, respond to customers, and track enquiries from submission through resolution."
      >
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="btn-secondary"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          Refresh enquiries
        </button>
      </PageHeader>

      {/* FEEDBACK */}
      <SuccessMessage>{success}</SuccessMessage>
      <ErrorMessage>{error}</ErrorMessage>

      {/* STATISTICS */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard
          title="Total enquiries"
          value={statistics.total}
          description="All website submissions"
          icon={Inbox}
          iconClass="bg-brand-50 text-brand-700"
        />

        <StatCard
          title="New enquiries"
          value={statistics.newCount}
          description="Awaiting review"
          icon={MessageSquareText}
          iconClass="bg-accent-50 text-accent-700"
        />

        <StatCard
          title="In progress"
          value={statistics.inProgressCount}
          description="Currently being handled"
          icon={Clock3}
          iconClass="bg-amber-50 text-amber-600"
        />

        <StatCard
          title="Resolved"
          value={statistics.resolvedCount}
          description="Completed enquiries"
          icon={CheckCircle2}
          iconClass="bg-emerald-50 text-emerald-600"
        />
      </div>

      {/* ENQUIRY MANAGEMENT */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {/* TOOLBAR */}
        <div className="border-b border-slate-100 p-4 sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="font-semibold text-slate-900">
                All messages
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                Search, review, and update incoming enquiries.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600">
                {filtered.length} result
                {filtered.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_190px_190px]">
            {/* SEARCH */}
            <div className="relative">
              <Search
                size={17}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500"
              />

              <input
                className={`${inputClass} pl-10`}
                placeholder="Search name, email, phone, subject..."
                value={query}
                onChange={(event) =>
                  setQuery(event.target.value)
                }
              />
            </div>

            {/* STATUS FILTER */}
            <div className="relative">
              <select
                className={`${inputClass} appearance-none pr-10`}
                value={status}
                onChange={(event) =>
                  setStatus(event.target.value)
                }
              >
                <option value="all">All statuses</option>
                <option value="new">New</option>
                <option value="in-progress">In progress</option>
                <option value="resolved">Resolved</option>
              </select>

              <ChevronDown
                size={16}
                className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500"
              />
            </div>

            {/* SORT */}
            <div className="relative">
              <select
                className={`${inputClass} appearance-none pr-10`}
                value={sortBy}
                onChange={(event) =>
                  setSortBy(event.target.value)
                }
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="name">Sort by name</option>
              </select>

              <ChevronDown
                size={16}
                className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500"
              />
            </div>
          </div>

          {(query || status !== "all") && (
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">
                Filters are applied to your enquiry list.
              </p>

              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setStatus("all");
                }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-800"
              >
                <X size={13} />
                Clear filters
              </button>
            </div>
          )}
        </div>

        {/* TABLE */}
        {loading ? (
          <div className="p-6">
            <Loading label="Loading contact enquiries..." />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-5 sm:p-8">
            <EmptyState
              title={
                query || status !== "all"
                  ? "No matching enquiries"
                  : "No enquiries yet"
              }
              detail={
                query || status !== "all"
                  ? "Try another search term or change the status filter."
                  : "Messages submitted through your website contact form will appear here."
              }
            />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead>
                  <tr>
                    <th className="table-head px-5 py-3">
                      Contact
                    </th>

                    <th className="table-head px-5 py-3">
                      Subject / message
                    </th>

                    <th className="table-head px-5 py-3">
                      Received
                    </th>

                    <th className="table-head px-5 py-3">
                      Status
                    </th>

                    <th className="table-head px-5 py-3 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.map((item) => (
                    <tr
                      key={item._id}
                      className="transition hover:bg-slate-50/70"
                    >
                      {/* CONTACT */}
                      <td className="table-cell">
                        <ContactInfo item={item} />
                      </td>

                      {/* MESSAGE */}
                      <td className="table-cell">
                        <p className="font-semibold text-slate-800">
                          {item.subject || "No subject"}
                        </p>

                        <MessagePreview message={item.message} />
                      </td>

                      {/* DATE */}
                      <td className="table-cell whitespace-nowrap">
                        <p className="text-sm font-medium text-slate-700">
                          {formatDate(item.createdAt)}
                        </p>

                        <p className="mt-1 text-xs text-slate-500">
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleTimeString(
                                undefined,
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )
                            : "—"}
                        </p>
                      </td>

                      {/* STATUS */}
                      <td className="table-cell">
                        <div className="space-y-2">
                          <StatusBadge
                            status={item.status || "new"}
                          />

                          <div className="relative">
                            <select
                              aria-label={`Status for ${item.name || "contact"}`}
                              className="max-w-36 cursor-pointer rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-medium text-slate-600 outline-none focus:border-brand-500"
                              value={item.status || "new"}
                              onChange={(event) =>
                                updateStatus(
                                  item,
                                  event.target.value
                                )
                              }
                              disabled={saving}
                            >
                              <option value="new">New</option>
                              <option value="in-progress">
                                In progress
                              </option>
                              <option value="resolved">
                                Resolved
                              </option>
                            </select>
                          </div>
                        </div>
                      </td>

                      {/* ACTIONS */}
                      <td className="table-cell">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setSelected(item)}
                            className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:border-accent-100 hover:bg-accent-50 hover:text-accent-700"
                            title="View enquiry"
                            aria-label={`View enquiry from ${item.name || "contact"}`}
                          >
                            <Eye size={16} />
                          </button>

                          {item.email && (
                            <a
                              href={`mailto:${item.email}?subject=${encodeURIComponent(
                                `Re: ${item.subject || "Your enquiry"}`
                              )}`}
                              className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:border-brand-200 hover:bg-brand-50 hover:text-brand-800"
                              title="Reply by email"
                              aria-label={`Email ${item.name || "contact"}`}
                            >
                              <Mail size={16} />
                            </a>
                          )}

                          <button
                            type="button"
                            onClick={() => setDeleteTarget(item)}
                            className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                            title="Delete enquiry"
                            aria-label={`Delete enquiry from ${item.name || "contact"}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* TABLE FOOTER */}
            <div className="flex flex-col gap-2 border-t border-slate-100 px-5 py-3 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
              <span>
                Showing {filtered.length} of {items.length} enquiries
              </span>

              <span>
                Status changes are saved through the backend API.
              </span>
            </div>
          </>
        )}
      </div>

      {/* =====================================================
          VIEW ENQUIRY MODAL
      ===================================================== */}

      <Modal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title="Contact enquiry details"
        wide
      >
        {selected && (
          <div className="space-y-5">
            {/* Contact heading */}
            <div className="rounded-xl bg-gradient-to-r from-brand-50 to-accent-50 p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-white text-brand-700 shadow-sm">
                  <UserRound size={23} />
                </div>

                <div className="min-w-0 flex-1">
                  <h3 className="break-words text-lg font-semibold text-slate-900">
                    {selected.name || "Unknown contact"}
                  </h3>

                  <p className="mt-1 break-all text-sm text-slate-600">
                    {selected.email || "No email provided"}
                  </p>

                  <div className="mt-3">
                    <StatusBadge
                      status={selected.status || "new"}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact information */}
            <section>
              <h3 className="mb-3 text-sm font-semibold text-slate-900">
                Contact information
              </h3>

              <div className="grid gap-3 sm:grid-cols-2">
                <DetailField
                  label="Full name"
                  value={selected.name}
                  icon={UserRound}
                />

                <DetailField
                  label="Email address"
                  value={selected.email}
                  icon={Mail}
                />

                <DetailField
                  label="Mobile number"
                  value={selected.mobile}
                  icon={Phone}
                />

                <DetailField
                  label="Date received"
                  value={formatDateTime(selected.createdAt)}
                  icon={CalendarDays}
                />
              </div>
            </section>

            {/* Subject */}
            <section>
              <h3 className="mb-3 text-sm font-semibold text-slate-900">
                Enquiry subject
              </h3>

              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="break-words text-sm font-semibold text-slate-800">
                  {selected.subject || "No subject provided"}
                </p>
              </div>
            </section>

            {/* Message */}
            <section>
              <h3 className="mb-3 text-sm font-semibold text-slate-900">
                Message
              </h3>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 sm:p-5">
                <p className="whitespace-pre-wrap break-words text-sm leading-7 text-slate-700">
                  {selected.message || "No message provided."}
                </p>
              </div>
            </section>

            {/* Update status */}
            <section>
              <h3 className="mb-3 text-sm font-semibold text-slate-900">
                Update enquiry status
              </h3>

              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <label
                  htmlFor="contact-status"
                  className="mb-2 block text-sm font-medium text-slate-700"
                >
                  Current status
                </label>

                <select
                  id="contact-status"
                  className={inputClass}
                  value={selected.status || "new"}
                  onChange={(event) =>
                    updateStatus(selected, event.target.value)
                  }
                  disabled={saving}
                >
                  <option value="new">New</option>
                  <option value="in-progress">In progress</option>
                  <option value="resolved">Resolved</option>
                </select>

                <p className="mt-2 text-xs leading-5 text-slate-500">
                  Changes are saved immediately to the server.
                </p>
              </div>
            </section>

            {/* Footer actions */}
            <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-between">
              <button
                type="button"
                onClick={() => {
                  setSelected(null);
                  setDeleteTarget(selected);
                }}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50"
              >
                <Trash2 size={15} />
                Delete enquiry
              </button>

              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                {selected.email && (
                  <a
                    href={`mailto:${selected.email}?subject=${encodeURIComponent(
                      `Re: ${selected.subject || "Your enquiry"}`
                    )}`}
                    className="btn-primary justify-center"
                  >
                    <Mail size={15} />
                    Reply by email
                    <ExternalLink size={13} />
                  </a>
                )}

                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="btn-secondary justify-center"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* =====================================================
          DELETE CONFIRMATION MODAL
      ===================================================== */}

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        title="Delete contact enquiry"
      >
        <div className="space-y-5">
          <div className="flex items-start gap-3 rounded-lg border border-red-100 bg-red-50 p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-red-600">
              <AlertCircle size={20} />
            </div>

            <div>
              <h3 className="font-semibold text-red-900">
                Are you sure?
              </h3>

              <p className="mt-1 text-sm leading-6 text-red-800">
                Delete the enquiry from{" "}
                <strong>
                  {deleteTarget?.name || "this contact"}
                </strong>
                ? This action cannot be undone.
              </p>
            </div>
          </div>

          {deleteTarget && (
            <div className="rounded-lg border border-slate-200 p-4">
              <p className="text-sm font-semibold text-slate-800">
                {deleteTarget.name || "Unknown contact"}
              </p>

              {deleteTarget.email && (
                <p className="mt-1 break-all text-xs text-slate-500">
                  {deleteTarget.email}
                </p>
              )}

              {deleteTarget.subject && (
                <p className="mt-2 text-sm text-slate-600">
                  {deleteTarget.subject}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="btn-secondary justify-center"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={remove}
              disabled={deleting}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {deleting ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={16} />
                  Delete enquiry
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
